import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import DashboardLayout from "../../common/DashboardLayout";
import OrgAdminServices from "../../../services/OrgAdminServices";
import "./orgAdmins.css";

/**
 * Organization Administrators — sysadmin only.
 *
 * One person, one email address, several schools. Head Start California is ~140
 * grantees across ~1,710 sites and cannot be onboarded as a separate login per
 * site; the twelve-school customer cannot either. Both were previously told to
 * use plus-addressed emails, which is not a thing a customer should have to do.
 *
 * The role has existed since July and worked the whole time — what did not
 * exist was any way to give it to somebody. This is that.
 *
 * Deliberately a flat list of schools. There is no organization table yet, and
 * adding one before anybody is using this would be building the hierarchy
 * before knowing what shape it needs to be.
 */

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

const fullName = (u) => (u.name || "").trim() || u.email;

const OrgAdmins = () => {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState("");

  // The person being edited: { id, email, name, schoolIds }. null = the list.
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState([]);          // ids, while editing
  const [schoolFilter, setSchoolFilter] = useState("");
  const [saving, setSaving] = useState(false);

  // Adding somebody new
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const latest = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrgAdminServices.list();
      setAdmins(res.admins || []);
      setSchools(res.schools || []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load organization administrators.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const schoolsById = useMemo(
    () => new Map(schools.map((s) => [s.id, s])), [schools]
  );

  // ---- finding somebody to give the role to -------------------------------

  const runSearch = useCallback(async (q) => {
    const seq = ++latest.current;
    setSearching(true);
    try {
      const res = await OrgAdminServices.searchUsers(q);
      if (seq !== latest.current) return;
      /**
       * search-users also returns School-model rows. Those are not users —
       * setMultiSchoolAdmin looks the id up in the user table and would answer
       * "User not found", which reads as a bug rather than as the wrong kind of
       * row having been offered. So they are not offered.
       */
      setResults(((res && res.users) || []).filter((u) => u.type !== "school"));
      setSearched(true);
    } catch (err) {
      if (seq !== latest.current) return;
      setError(err?.response?.data?.error || "Search failed.");
      setResults([]);
    } finally {
      if (seq === latest.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!adding) return undefined;
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      latest.current += 1;
      setResults([]); setSearched(false); setSearching(false);
      return undefined;
    }
    const t = setTimeout(() => runSearch(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, adding, runSearch]);

  // ---- editing one person's schools ---------------------------------------

  function edit(admin) {
    setEditing(admin);
    setPicked((admin.schoolIds || []).filter((id) => schoolsById.has(id)));
    setSchoolFilter("");
    setAdding(false);
    setError("");
  }

  function startWith(user) {
    const already = admins.find((a) => a.id === user.id);
    // Somebody can turn up in the search who already holds the role. Editing
    // them is what was meant, and it keeps the schools they already have
    // instead of starting them from nothing.
    if (already) return edit(already);
    edit({ id: user.id, email: user.email, name: fullName(user), schoolIds: [], isNew: true });
  }

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const visibleSchools = useMemo(() => {
    const f = schoolFilter.trim().toLowerCase();
    if (!f) return schools;
    return schools.filter(
      (s) => (s.name || "").toLowerCase().includes(f)
        || (s.address || "").toLowerCase().includes(f)
    );
  }, [schools, schoolFilter]);

  async function save() {
    if (!editing) return;
    const who = fullName(editing);

    if (picked.length === 0) {
      const { isConfirmed } = await Swal.fire({
        title: `Remove ${who} as an organization administrator?`,
        html:
          "<div class='text-start' style='font-size:13.4px;line-height:1.6'>"
          + "They keep every other role they hold — somebody who was a school "
          + "administrator before this goes back to being one, and does not lose "
          + "their access. Their teachers, certificates and history are untouched."
          + "</div>",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remove the role",
        confirmButtonColor: "#B4453A"
      });
      if (!isConfirmed) return;
    }

    setSaving(true); setError("");
    try {
      await OrgAdminServices.save(editing.id, picked);
      await load();
      setEditing(null);
      Swal.fire({
        icon: "success",
        title: picked.length === 0 ? "Role removed" : "Saved",
        text: picked.length === 0
          ? `${who} is no longer an organization administrator.`
          : `${who} administers ${picked.length} school${picked.length === 1 ? "" : "s"}.`,
        timer: 2200,
        showConfirmButton: false
      });
    } catch (err) {
      const data = err?.response?.data;
      // The server names the schools it refused. Saying which is the difference
      // between a fixable message and "something went wrong".
      const missing = (data && data.missing) || [];
      setError(
        missing.length
          ? `${missing.length} of the selected schools no longer exist and were refused: ${missing.join(", ")}`
          : (data && data.error) || "Could not save."
      );
    } finally {
      setSaving(false);
    }
  }

  // ---- render --------------------------------------------------------------

  const q = query.trim();

  return (
    <DashboardLayout role="admin" layoutType="topnav" activeMenu="User Management" showBreadcrumbs={false}>
      <div className="oa-page">
        <div className="oa-head">
          <h2>Organization Administrators</h2>
          <p>
            One login that administers several schools. An organization administrator
            signs in with their own email address and switches between their schools
            from the top of every screen — no second account, and no
            <span className="oa-mono">+name@</span> addresses.
          </p>
        </div>

        {error ? <div className="oa-alert">{error}</div> : null}

        {loading ? <div className="oa-muted">Loading…</div> : null}

        {/* ---------------- the list ---------------- */}
        {!loading && !editing && (
          <>
            <div className="oa-bar">
              <div className="oa-count">
                {admins.length === 0
                  ? "Nobody has this role yet."
                  : `${admins.length} organization administrator${admins.length === 1 ? "" : "s"}`}
              </div>
              <button type="button" className="btn btn-sm btn-primary oa-add" onClick={() => { setAdding(true); setQuery(""); setResults([]); setSearched(false); }}>
                Add organization administrator
              </button>
            </div>

            {adding && (
              <div className="oa-panel">
                <label className="oa-label" htmlFor="oa-user-search">
                  Who should administer several schools?
                </label>
                <div className="oa-search">
                  <input
                    id="oa-user-search"
                    type="text"
                    value={query}
                    autoFocus
                    placeholder="Start typing a name or email"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {searching ? <span className="oa-spin" aria-label="searching" /> : null}
                </div>

                {q.length > 0 && q.length < MIN_CHARS ? (
                  <div className="oa-muted">Keep typing — at least {MIN_CHARS} characters.</div>
                ) : null}

                {searched && results.length === 0 && !searching ? (
                  <div className="oa-muted">No users matched “{q}”.</div>
                ) : null}

                {results.length > 0 && (
                  <ul className="oa-results">
                    {results.map((u) => {
                      const school = schoolsById.get(u.schoolId);
                      const held = admins.some((a) => a.id === u.id);
                      return (
                        <li key={u.id}>
                          <div>
                            <b>{fullName(u)}</b>
                            <span className="oa-sub">{u.email}</span>
                            {school ? <span className="oa-sub">{school.name}</span> : null}
                          </div>
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startWith(u)}>
                            {held ? "Edit their schools" : "Choose schools"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <button type="button" className="oa-link" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            )}

            {admins.length > 0 && (
              <div className="oa-list">
                {admins.map((a) => (
                  <div className="oa-card" key={a.id}>
                    <div className="oa-card-main">
                      <div className="oa-name">{a.name}</div>
                      <div className="oa-email">{a.email}</div>
                      <div className="oa-chips">
                        {a.schools.map((s) => <span className="oa-chip" key={s.id}>{s.name}</span>)}
                        {a.danglingSchoolIds && a.danglingSchoolIds.length ? (
                          /* A school deleted after the scope was set. Shown rather
                             than dropped, so the count agrees with the list. */
                          <span className="oa-chip oa-chip-gone">
                            {a.danglingSchoolIds.length} deleted school
                            {a.danglingSchoolIds.length === 1 ? "" : "s"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => edit(a)}>
                      Manage schools
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ---------------- the picker ---------------- */}
        {!loading && editing && (
          <div className="oa-panel">
            <div className="oa-editing">
              <div>
                <div className="oa-name">{fullName(editing)}</div>
                <div className="oa-email">{editing.email}</div>
              </div>
              <button type="button" className="oa-link" onClick={() => setEditing(null)}>Back</button>
            </div>

            <p className="oa-note">
              Tick every school this person should administer. The list replaces what
              they had, so unticking a school removes it. Unticking all of them removes
              the role — they keep everything else they are.
            </p>

            <div className="oa-search">
              <input
                type="text"
                value={schoolFilter}
                placeholder={`Filter ${schools.length} schools by name or address`}
                onChange={(e) => setSchoolFilter(e.target.value)}
              />
            </div>

            <div className="oa-schools">
              {visibleSchools.length === 0 ? (
                <div className="oa-muted oa-pad">No schools matched that.</div>
              ) : visibleSchools.map((s) => (
                <label className={`oa-school${picked.includes(s.id) ? " is-picked" : ""}`} key={s.id}>
                  <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} />
                  <span className="oa-school-name">{s.name}</span>
                  {s.address ? <span className="oa-school-sub">{s.address}</span> : null}
                  {s.customerStatus && s.customerStatus !== "paying"
                    ? <span className="oa-status">{s.customerStatus}</span> : null}
                </label>
              ))}
            </div>

            <div className="oa-foot">
              <div className="oa-count">
                {picked.length === 0
                  ? "No schools selected — saving removes the role."
                  : `${picked.length} school${picked.length === 1 ? "" : "s"} selected`}
              </div>
              <div className="oa-actions">
                <button type="button" className="btn btn-sm btn-light" onClick={() => setEditing(null)} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${picked.length === 0 ? "btn-outline-danger" : "btn-primary"}`}
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving…" : picked.length === 0 ? "Remove the role" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="oa-info">
          <b>What an organization administrator can do.</b> Everything a school
          administrator can do, at every school in their list, switching between them
          from the top of the screen. They cannot see a school that is not in their
          list, and they cannot grant this role to anybody else — only a system
          administrator can, from this screen.
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OrgAdmins;
